<template>
  <div
    :class="{ disabled: disabled }"
    class="multiple-select border rounded border-color-light-gray"
  >
    <div class="bg-light-gray p-2">
      <v-select
        v-model="selectedOption"
        class="w-100"
        :disabled="disabled"
        :class="
          onDemand && filteredOptions.length == 0 ? 'picker-hide-options' : ''
        "
        :placeholder="placeholder"
        :options="filteredOptions"
        :filterable="!disableFilter"
        :async="search != null"
        @search="
          (searchStr, loading) => {
            if (search) {
              search(searchStr, loading);
            }
          }
        "
      >
        <template #option="{ data }">
          <slot name="option" v-bind:data="data"></slot>
        </template>
        <template #selected-option="{ data }">
          <slot name="selected-option" v-bind:data="data"></slot>
        </template>
        <template #spinner>
          <div></div>
        </template>
        <template #list-footer>
          <li v-if="loading" class="vs__spinner"></li>
        </template>
        <template #no-options>
          <li v-if="!loading">Sorry, no matching options</li>
          <li v-else>&nbsp;</li>
        </template>
      </v-select>
    </div>
    <div class="select-list-container" :style="{ maxHeight: maxHeight }">
      <b-list-group v-if="value.length > 0" class="mt-1 p-1 forms-list">
        <draggable v-if="listDraggable" v-model="valueReorder">
          <b-list-group-item
            class="filter-value border pl-2 pr-2 pt-1 pb-1 mr-2 ml-1 mt-2"
            v-for="(valueItem, valueItemIndex) in value"
            :key="valueItemIndex"
          >
            <div>
              <b-icon-grip-horizontal></b-icon-grip-horizontal>
              {{ valueItem.label }}
            </div>
            <img
              v-show="!disabled"
              class="filter-value-delete"
              :src="require('../../assets/icons/close-circle.png')"
              @click="removeSelected(valueItemIndex)"
            />
          </b-list-group-item>
        </draggable>
        <div v-else>
          <b-list-group-item
            class="filter-value border pl-2 pr-2 pt-1 pb-1 mr-2 ml-1 mt-2"
            v-for="(valueItem, valueItemIndex) in value"
            :key="valueItemIndex"
          >
            <slot name="selected-option" v-bind:data="valueItem.data">
              <div>
                <span v-html-sanitize="valueItem.label"></span>
              </div>
            </slot>
            <img
              v-show="!disabled"
              class="filter-value-delete"
              :src="require('../../assets/icons/close-circle.png')"
              @click="removeSelected(valueItemIndex)"
            />
          </b-list-group-item>
        </div>
      </b-list-group>
      <div v-else class="text-center color-custom-dark-grey m-1">
        No items selected.
      </div>
    </div>
  </div>
</template>

<script>
import Draggable from "vuedraggable";

const itemEquality = (a, b) => a.value == b.value && a.label == b.label;

export default {
  name: "MultipleSelect",
  components: { Draggable },
  props: {
    value: {
      type: Array,
      required: true
    },
    options: {
      type: Array,
      required: true
    },
    listDraggable: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    search: {
      type: Function,
      default: null
    },
    loading: {
      type: Boolean
    },
    sortFn: {
      type: Function,
      default: (a, b) => a.label.localeCompare(b.label)
    },
    onDemand: {
      type: Boolean,
      default: false
    },
    allowDuplicates: {
      type: Boolean,
      default: false
    },
    disableCache: {
      type: Boolean,
      default: false
    },
    maxHeight: {
      type: String,
      default: "200px"
    },
    disableFilter: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      cachedOptions: []
    };
  },
  mounted() {
    this.addOptionsToCache(this.options);
  },
  methods: {
    removeSelected(itemIndex) {
      const value = [...this.value];
      const removedValue = value[itemIndex].value;
      this.$delete(this.value, itemIndex);
      this.$emit("optionRemoved", removedValue);
    },
    emit(value) {
      this.$emit("input", [...value]);
    },
    addOptionsToCache(options) {
      if (!options) {
        return;
      }

      if (this.disableCache) {
        this.cachedOptions = [...options].sort(this.sortFn);
      } else {
        this.cachedOptions = options
          .filter(
            item =>
              !this.cachedOptions.some(cached => cached.value == item.value)
          )
          .concat(this.cachedOptions)
          .sort(this.sortFn);
      }
    }
  },
  computed: {
    selectedOption: {
      get() {
        return null;
      },
      set(newValue) {
        if (newValue) {
          const value = [...this.value];
          value.push(newValue);
          this.emit(value);
        }
      }
    },
    valueReorder: {
      get() {
        return this.value;
      },
      set(newValue) {
        const value = [...newValue];
        this.emit(value);
      }
    },
    filteredOptions() {
      if (this.allowDuplicates) return this.cachedOptions;
      return this.cachedOptions.filter(
        opt => !this.value.some(o => itemEquality(opt, o))
      );
    }
  },
  watch: {
    options(newValue) {
      this.addOptionsToCache(newValue);
    }
  }
};
</script>

<style scoped>
.select-list-container {
  overflow-y: auto;
  overflow-x: hidden;
}
.filter-value {
  position: relative;
  min-width: 100px;
  color: #a6a6a6;
  font-size: 14px;
}
.filter-value-delete {
  position: absolute;
  top: -8px;
  right: -8px;
}
.filter-value-delete:hover {
  cursor: pointer;
}

.multiple-select >>> .vs--single .vs__selected {
  z-index: 10;
}
.multiple-select >>> .vs--single.vs--open .vs__selected {
  position: initial;
}
.multiple-select >>> .vs__spinner {
  opacity: 1;
  border-left-color: rgba(88, 151, 251, 0.71);
  padding: 0.5em;
  text-align: center;
  margin: 0.5em auto;
}
.multiple-select >>> .vs__selected-options input {
  position: absolute;
  width: 100%;
  height: 100%;
}
.picker-hide-options .vs__dropdown-menu {
  display: none;
}
.picker-hide-options.vs--open .vs__dropdown-toggle {
  border-bottom-color: #4044e6 !important;
  border-bottom-width: 2px;
}
.disabled .select-list-container,
.disabled .select-list-container .list-group,
.disabled .select-list-container .list-group .list-group-item {
  background-color: #e7e7e7;
}
.multiple-select >>> .vs__dropdown-menu li {
  white-space: normal;
}
</style>
